/**
 * AircraftInterpolator.ts
 *
 * Dead-reckoning extrapolator for live aircraft positions between 10-second polls.
 *
 * Usage:
 *   const interp = new AircraftInterpolator(pushFn);
 *   // On each poll response:
 *   interp.updateSnapshots(parseOpenSkyStates(resp.states));
 *   interp.start(); // starts rAF loop that calls pushFn ~60fps
 *   // On unmount:
 *   interp.stop();
 *
 * The rAF loop calls pushFn(layerId, buf, count) on every animation frame,
 * extrapolating each aircraft's position based on its last known heading and speed.
 */

import { AircraftSnapshot, AIRCRAFT_STRIDE } from './openSkyConnector';

type PushFn = (layerId: string, data: Float32Array, count: number) => void;

/** Maximum extrapolation window before position is considered stale (seconds) */
const MAX_DT_SEC = 15;

/**
 * AircraftInterpolator manages a per-aircraft snapshot map and drives
 * smooth 60fps position updates via requestAnimationFrame.
 *
 * Dead-reckoning algorithm (Pattern 2 from RESEARCH.md):
 *   dtSec = min((nowMs - snap.timestampMs) / 1000, 15)
 *   headingRad = snap.headingDeg * PI / 180
 *   distM = snap.velocityMs * dtSec
 *   dLat = (distM / 111_000) * cos(headingRad)
 *   dLon = (distM / (111_000 * cos(snap.lat * PI / 180))) * sin(headingRad)
 */
export class AircraftInterpolator {
  private snapshots: Map<string, AircraftSnapshot>;
  private rafId: number | null;
  private layerId: string;
  private pushFn: PushFn;

  constructor(pushFn: PushFn) {
    this.pushFn = pushFn;
    this.snapshots = new Map();
    this.rafId = null;
    this.layerId = 'aircraft';
  }

  /**
   * Update (or insert) snapshots by icao24 key.
   * Called on each 10-second poll response.
   */
  updateSnapshots(snapshots: AircraftSnapshot[]): void {
    for (const snap of snapshots) {
      this.snapshots.set(snap.icao24, snap);
    }
  }

  /**
   * Returns the number of currently tracked aircraft.
   */
  getCount(): number {
    return this.snapshots.size;
  }

  /**
   * Build stride-5 Float32Array with extrapolated positions for all tracked aircraft.
   * Public — used in tests to inspect output without side effects.
   *
   * @param nowMs - Current time in milliseconds (Date.now())
   */
  buildFloat32Array(nowMs: number): Float32Array {
    const count = this.snapshots.size;
    const buf = new Float32Array(count * AIRCRAFT_STRIDE);
    let idx = 0;

    this.snapshots.forEach((snap) => {
      const { lon, lat } = this.extrapolatePosition(snap, nowMs);
      const normSpeed = Math.min(snap.velocityMs / 250, 1.0);
      const rotRad = (snap.headingDeg * Math.PI) / 180;

      buf[idx * AIRCRAFT_STRIDE + 0] = lon;
      buf[idx * AIRCRAFT_STRIDE + 1] = lat;
      buf[idx * AIRCRAFT_STRIDE + 2] = snap.altM;
      buf[idx * AIRCRAFT_STRIDE + 3] = normSpeed;
      buf[idx * AIRCRAFT_STRIDE + 4] = rotRad;
      idx++;
    });

    return buf;
  }

  /**
   * Start the requestAnimationFrame loop.
   * Calls pushFn with extrapolated positions ~60 times per second.
   */
  start(): void {
    if (this.rafId !== null) return; // already running

    const loop = () => {
      const nowMs = Date.now();
      const count = this.snapshots.size;
      if (count > 0) {
        const buf = this.buildFloat32Array(nowMs);
        this.pushFn(this.layerId, buf, count);
      }
      this.rafId = requestAnimationFrame(loop);
    };

    this.rafId = requestAnimationFrame(loop);
  }

  /**
   * Stop the rAF loop. Safe to call even if not started.
   */
  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Dead-reckoning position extrapolation for a single aircraft.
   * dt is clamped to MAX_DT_SEC (15s) to avoid large position errors for stale data.
   */
  private extrapolatePosition(snap: AircraftSnapshot, nowMs: number): { lon: number; lat: number } {
    const dtSec = (nowMs - snap.timestampMs) / 1000;
    const clampedDt = Math.min(dtSec, MAX_DT_SEC);

    if (clampedDt <= 0 || snap.velocityMs <= 0) {
      return { lon: snap.lon, lat: snap.lat };
    }

    const headingRad = (snap.headingDeg * Math.PI) / 180;
    const distM = snap.velocityMs * clampedDt;

    // Approximate: 1 degree latitude ≈ 111 km
    const distDegLat = distM / 111_000;
    // Longitude degrees per meter shrink with cos(lat)
    const cosLat = Math.cos(snap.lat * (Math.PI / 180));
    const distDegLon = cosLat !== 0 ? distM / (111_000 * cosLat) : 0;

    // North=0°, clockwise: dx = sin(heading), dy = cos(heading)
    const dLon = distDegLon * Math.sin(headingRad);
    const dLat = distDegLat * Math.cos(headingRad);

    return { lon: snap.lon + dLon, lat: snap.lat + dLat };
  }
}
